import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import EconomicCalendarExplorer from '../../components/calendar/EconomicCalendarExplorer';
import Footer from '../../components/feature/Footer';
import Header from '../../components/feature/Header';
import LiveMarketNewsCard from '../../components/news/LiveMarketNewsCard';
import NewsCard from '../../components/news/NewsCard';
import { goldNews } from '../../data/goldNews';
import {
  fetchMarketNews,
  getMarketContentLanguage,
  marketDataConfig,
  type MarketNewsItem,
} from '../../services/marketDataApi';

type LangMode = 'zh-HK' | 'zh-CN' | 'en';
type ArchiveFilter = 'all' | 'gold' | 'markets' | 'rates' | 'central-bank' | 'logistics';
type LiveFilter = 'ALL' | 'GENERAL' | 'FOREX' | 'STOCK';
type PageTab = 'news' | 'calendar';

const NEWS_LOCALES: Record<string, string> = {
  hk: 'zh-HK',
  cn: 'zh-CN',
  en: 'en-GB',
  jp: 'ja-JP',
  kr: 'ko-KR',
  th: 'th-TH',
  vn: 'vi-VN',
  de: 'de-DE',
  fr: 'fr-FR',
  it: 'it-IT',
  es: 'es-ES',
};

const newsLocale = (language: string) => NEWS_LOCALES[language.split('-')[0]] || 'en-GB';

export default function NewsPage() {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const apiLanguage = getMarketContentLanguage(i18n.resolvedLanguage || i18n.language);
  const activeTab: PageTab = searchParams.get('tab') === 'calendar' ? 'calendar' : 'news';
  const tabs = {
    news: t('news.tabs.news'),
    calendar: t('news.tabs.calendar'),
    newsDescription: t('news.tab.newsDesc', {
      defaultValue: 'Latest market headlines, gold analysis and educational content',
    }),
    calendarDescription: t('news.tab.calendarDesc', {
      defaultValue: 'Review important economic events and releases across nearby dates',
    }),
  };

  const [liveItems, setLiveItems] = useState<MarketNewsItem[]>([]);
  const [liveFilter, setLiveFilter] = useState<LiveFilter>('ALL');
  const [liveKeyword, setLiveKeyword] = useState('');
  const [liveLoading, setLiveLoading] = useState(true);
  const [liveError, setLiveError] = useState('');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const [langMode, setLangMode] = useState<LangMode>(() => {
    if (i18n.language === 'cn') return 'zh-CN';
    if (i18n.language === 'hk') return 'zh-HK';
    return 'en';
  });
  const [archiveFilter, setArchiveFilter] = useState<ArchiveFilter>('all');
  const [archiveKeyword, setArchiveKeyword] = useState('');

  useEffect(() => {
    if (i18n.language === 'cn') setLangMode('zh-CN');
    else if (i18n.language === 'hk') setLangMode('zh-HK');
    else setLangMode('en');
  }, [i18n.language]);

  useEffect(() => {
    if (activeTab !== 'news') return undefined;

    let mounted = true;
    let activeController: AbortController | null = null;

    const load = async (showLoading: boolean) => {
      activeController?.abort();
      const controller = new AbortController();
      activeController = controller;
      if (showLoading && mounted) setLiveLoading(true);
      if (mounted) setLiveError('');

      try {
        const response = await fetchMarketNews(
          { type: 'ALL', page: 0, size: 60, language: apiLanguage },
          controller.signal,
        );
        if (!mounted) return;
        setLiveItems(response.items);
        setLastUpdated(new Date());
      } catch (requestError) {
        if (!mounted || controller.signal.aborted) return;
        setLiveError(
          requestError instanceof Error
            ? requestError.message
            : 'Latest market news is temporarily unavailable.',
        );
      } finally {
        if (mounted) setLiveLoading(false);
      }
    };

    void load(true);
    const timer = window.setInterval(() => void load(false), marketDataConfig.contentRefreshMs);

    return () => {
      mounted = false;
      activeController?.abort();
      window.clearInterval(timer);
    };
  }, [activeTab, apiLanguage]);

  const filteredLiveItems = useMemo(() => {
    const keyword = liveKeyword.trim().toLowerCase();
    return liveItems.filter((item) => {
      const matchesType = liveFilter === 'ALL' || item.type?.toUpperCase() === liveFilter;
      if (!matchesType) return false;
      if (!keyword) return true;
      return [item.title, item.snippet, item.symbols]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(keyword);
    });
  }, [liveItems, liveFilter, liveKeyword]);

  const filteredArchive = useMemo(() => {
    const keyword = archiveKeyword.trim().toLowerCase();
    return goldNews
      .filter((item) => (archiveFilter === 'all' ? true : item.category === archiveFilter))
      .filter((item) => {
        if (!keyword) return true;
        const block = item.lang[langMode];
        return (
          block.title.toLowerCase().includes(keyword) ||
          block.summary.toLowerCase().includes(keyword) ||
          block.content.toLowerCase().includes(keyword) ||
          item.tags.join(' ').toLowerCase().includes(keyword)
        );
      })
      .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
  }, [archiveFilter, archiveKeyword, langMode]);

  const archiveFilters: { key: ArchiveFilter; label: string }[] = [
    { key: 'all', label: t('news.filter.all') },
    { key: 'gold', label: t('news.filter.gold') },
    { key: 'markets', label: t('news.filter.markets') },
    { key: 'rates', label: t('news.filter.rates') },
    { key: 'central-bank', label: t('news.filter.centralBank') },
    { key: 'logistics', label: t('news.filter.logistics') },
  ];

  const selectTab = (tab: PageTab) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <main>
        <section className="relative overflow-hidden bg-gray-950 pb-20 pt-32 text-white md:pb-24 md:pt-40">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-25"
            style={{ backgroundImage: "url('/img/news/gold-01.jpg')" }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-gray-950/90 to-black/60" />
          <div className="absolute -right-20 top-8 h-80 w-80 rounded-full bg-orange-500/15 blur-3xl" />

          <div className="container relative mx-auto px-4 text-center md:px-6">
            <span className="inline-flex items-center gap-2 rounded-full border border-orange-300/30 bg-orange-400/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-orange-200">
              <i className="ri-global-line" />
              {t('home.mktInt')}
            </span>
            <h1 className="mt-6 text-4xl font-bold md:text-6xl">{t('nav.news')}</h1>
            <p className="mx-auto mt-5 max-w-3xl text-base leading-7 text-gray-300 md:text-xl">
              {t('news.subtitle')}
            </p>
            {activeTab === 'news' && (
              <p className="mt-4 text-xs text-gray-400">
                {lastUpdated
                  ? `${t('calendar.lastUpdated')}: ${new Intl.DateTimeFormat(newsLocale(i18n.language), {
                      timeZone: marketDataConfig.timeZone,
                      day: '2-digit',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false,
                    }).format(lastUpdated)} · ${t('calendar.hongKongTime')}`
                  : t('news.autoRefresh', {
                      defaultValue: 'News information refreshes automatically',
                    })}
              </p>
            )}
          </div>
        </section>

        <section id="market-intelligence-tabs" className="bg-gray-50 py-10 md:py-14">
          <div className="container mx-auto px-4 md:px-6">
            <div
              role="tablist"
              aria-label={t('news.marketIntelligence', { defaultValue: 'Market intelligence content' })}
              className="mx-auto mb-10 grid max-w-3xl grid-cols-1 gap-3 rounded-3xl border border-gray-200 bg-white p-3 shadow-lg sm:grid-cols-2"
            >
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'news'}
                onClick={() => selectTab('news')}
                className={`rounded-2xl px-5 py-4 text-left transition-all ${
                  activeTab === 'news'
                    ? 'bg-gray-900 text-white shadow-md'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span className="flex items-center gap-3 text-base font-bold">
                  <i className="ri-newspaper-line text-xl text-orange-400" />
                  {tabs.news}
                </span>
                <span className={`mt-1 block text-xs leading-5 ${activeTab === 'news' ? 'text-gray-300' : 'text-gray-500'}`}>
                  {tabs.newsDescription}
                </span>
              </button>

              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'calendar'}
                onClick={() => selectTab('calendar')}
                className={`rounded-2xl px-5 py-4 text-left transition-all ${
                  activeTab === 'calendar'
                    ? 'bg-orange-500 text-white shadow-md'
                    : 'bg-white text-gray-700 hover:bg-orange-50'
                }`}
              >
                <span className="flex items-center gap-3 text-base font-bold">
                  <i className={`ri-calendar-event-line text-xl ${activeTab === 'calendar' ? 'text-white' : 'text-orange-500'}`} />
                  {tabs.calendar}
                </span>
                <span className={`mt-1 block text-xs leading-5 ${activeTab === 'calendar' ? 'text-orange-50' : 'text-gray-500'}`}>
                  {tabs.calendarDescription}
                </span>
              </button>
            </div>

            {activeTab === 'calendar' ? (
              <EconomicCalendarExplorer />
            ) : (
              <div role="tabpanel" aria-label={tabs.news}>
                <div className="mb-8 rounded-3xl border border-gray-200 bg-white p-4 shadow-sm md:p-6">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex flex-wrap gap-2">
                      {(['ALL', 'GENERAL', 'FOREX', 'STOCK'] as LiveFilter[]).map((type) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => setLiveFilter(type)}
                          className={`rounded-full border px-4 py-2 text-sm font-semibold transition-all ${
                            liveFilter === type
                              ? 'border-yellow-500 bg-yellow-500 text-white'
                              : 'border-gray-200 bg-white text-gray-700 hover:border-yellow-400'
                          }`}
                        >
                          {t(`news.live.${type.toLowerCase()}`, { defaultValue: type === 'ALL'
                            ? 'All News'
                            : type === 'STOCK'
                              ? 'Stocks'
                              : type.charAt(0) + type.slice(1).toLowerCase() })}
                        </button>
                      ))}
                    </div>

                    <input
                      type="search"
                      value={liveKeyword}
                      onChange={(event) => setLiveKeyword(event.target.value)}
                      placeholder={t('news.live.search', { defaultValue: 'Search latest market news' })}
                      className="w-full rounded-2xl border border-gray-200 px-4 py-2 outline-none focus:border-yellow-500 lg:w-80"
                    />
                  </div>
                </div>

                {liveError && (
                  <div className="mb-8 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-center text-sm text-amber-800">
                    {t('news.live.unavailable', { defaultValue: liveError })}
                  </div>
                )}

                {liveLoading && liveItems.length === 0 ? (
                  <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                    {Array.from({ length: 6 }, (_, index) => (
                      <div
                        key={index}
                        className="animate-pulse overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-lg"
                      >
                        <div className="h-56 bg-gray-100" />
                        <div className="space-y-3 p-6">
                          <div className="h-5 w-4/5 rounded bg-gray-100" />
                          <div className="h-4 w-full rounded bg-gray-100" />
                          <div className="h-4 w-2/3 rounded bg-gray-100" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : filteredLiveItems.length > 0 ? (
                  <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                    {filteredLiveItems.map((item) => (
                      <LiveMarketNewsCard key={item.id} item={item} />
                    ))}
                  </div>
                ) : (
                  <div className="rounded-3xl border border-gray-200 bg-white p-10 text-center text-gray-500">
                    {t('news.live.empty', { defaultValue: 'No matching market news was found.' })}
                  </div>
                )}

                <div className="mt-20 border-t border-gray-200 pt-14">
                  <div className="mb-8 text-center">
                    <h2 className="text-3xl font-bold text-gray-900 md:text-4xl">{t('news.archive.title', { defaultValue: 'Gold Market Insights' })}</h2>
                    <p className="mt-3 text-gray-600">{t('news.archive.description', { defaultValue: 'Research articles and educational market commentary.' })}</p>
                  </div>

                  <div className="mb-8 rounded-3xl border border-gray-200 bg-white p-4 shadow-sm md:p-6">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                      <div className="flex flex-wrap gap-2">
                        {archiveFilters.map((item) => (
                          <button
                            key={item.key}
                            type="button"
                            onClick={() => setArchiveFilter(item.key)}
                            className={`rounded-full border px-4 py-2 text-sm font-semibold transition-all ${
                              archiveFilter === item.key
                                ? 'border-yellow-500 bg-yellow-500 text-white'
                                : 'border-gray-200 bg-white text-gray-700 hover:border-yellow-400'
                            }`}
                          >
                            {item.label}
                          </button>
                        ))}
                      </div>

                      <div className="flex flex-col gap-3 sm:flex-row">
                        <div className="flex overflow-hidden rounded-2xl border border-gray-200">
                          {(['zh-HK', 'zh-CN', 'en'] as LangMode[]).map((lang) => (
                            <button
                              key={lang}
                              type="button"
                              onClick={() => setLangMode(lang)}
                              className={`px-4 py-2 text-sm font-semibold transition-all ${
                                langMode === lang
                                  ? 'bg-gray-900 text-white'
                                  : 'bg-white text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              {lang === 'zh-HK' ? t('news.language.traditional', { defaultValue: '繁' }) : lang === 'zh-CN' ? t('news.language.simplified', { defaultValue: '简' }) : t('news.language.english', { defaultValue: 'EN' })}
                            </button>
                          ))}
                        </div>

                        <input
                          type="search"
                          value={archiveKeyword}
                          onChange={(event) => setArchiveKeyword(event.target.value)}
                          placeholder={t('news.searchPlaceholder')}
                          className="w-full rounded-2xl border border-gray-200 px-4 py-2 outline-none focus:border-yellow-500 sm:w-72"
                        />
                      </div>
                    </div>
                  </div>

                  {filteredArchive.length === 0 ? (
                    <div className="rounded-3xl border border-gray-200 bg-white p-10 text-center text-gray-500">
                      {t('news.empty')}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                      {filteredArchive.map((item) => (
                        <NewsCard key={item.id} item={item} lang={langMode} />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
